import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import ThemeToggle from './ThemeToggle';
import { Menu, X, Shield, ChevronDown } from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
  isExternal?: boolean;
  subItems?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  {
    label: 'About',
    href: '/about',
    subItems: [
      { label: 'Bit2terabyte', href: '/about' },
      { label: 'The Founder', href: '/sowrirajan' },
    ]
  },
  { label: 'Projects', href: '/#projects' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/#contact' },
];

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateTo = (href: string) => {
    setIsMobileMenuOpen(false);
    setIsAboutDropdownOpen(false);

    if (href.startsWith('/#') && location.pathname === '/') {
      // If we're already on the homepage and the link is to a section
      const element = document.querySelector(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Check if a nav item is active
  const isActive = (href: string) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && location.hash === href.substring(1);
    } else {
      return location.pathname === href || (href !== '/' && location.pathname.startsWith(href));
    }
  };

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 px-6 py-4 transition-all duration-300",
      isScrolled ? "glass-card backdrop-blur-lg bg-background/70" : "bg-transparent"
    )}>
      <div className="container mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 select-none">
          <Shield className="h-6 w-6 text-primary" />
          <span className="font-bold text-lg">Learn, Build & Scale</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative group"
              onMouseEnter={() => item.subItems && setIsAboutDropdownOpen(true)}
              onMouseLeave={() => item.subItems && setIsAboutDropdownOpen(false)}
            >
              {item.subItems ? (
                <div className="flex items-center cursor-pointer py-1">
                  <span className={cn(
                    "text-sm font-medium transition-colors",
                    item.subItems.some(sub => isActive(sub.href))
                      ? "text-primary"
                      : "text-foreground/80 hover:text-foreground"
                  )}>
                    {item.label}
                  </span>
                  <ChevronDown className={cn(
                    "h-4 w-4 ml-1 transition-transform duration-300",
                    isAboutDropdownOpen ? "rotate-180" : ""
                  )} />
                </div>
              ) : (
                <Link
                  to={item.href}
                  onClick={() => navigateTo(item.href)}
                  className={cn(
                    "text-sm font-medium py-1 transition-colors",
                    isActive(item.href)
                      ? "text-primary link-underline"
                      : "text-foreground/80 hover:text-foreground link-underline"
                  )}
                >
                  {item.label}
                </Link>
              )}

              {/* Dropdown Menu */}
              {item.subItems && (
                <div className={cn(
                  "absolute top-full left-0 mt-2 w-48 py-2 rounded-xl glass-card backdrop-blur-lg bg-background/90 border shadow-xl transition-all duration-300 transform origin-top",
                  isAboutDropdownOpen ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                )}>
                  {item.subItems.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      onClick={() => navigateTo(subItem.href)}
                      className={cn(
                        "block px-4 py-2 text-sm transition-colors",
                        isActive(subItem.href)
                          ? "text-primary bg-primary/5"
                          : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                      )}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <ThemeToggle />
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center space-x-4 md:hidden">
          <ThemeToggle />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          "fixed inset-0 bg-background/95 backdrop-blur-md z-40 flex flex-col items-center justify-center space-y-6 p-4 transition-all duration-300 ease-in-out transform",
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none translate-x-full"
        )}
      >
        {navItems.map((item, index) => (
          <React.Fragment key={item.label}>
            {item.subItems ? (
              <div className="flex flex-col items-center space-y-4">
                <span className="text-muted-foreground text-sm uppercase tracking-widest">{item.label}</span>
                {item.subItems.map((subItem, subIndex) => (
                  <Link
                    key={subItem.href}
                    to={subItem.href}
                    onClick={() => navigateTo(subItem.href)}
                    className={cn(
                      "text-xl font-medium transition-colors duration-300",
                      isActive(subItem.href) ? "text-primary" : "hover:text-primary",
                      isMobileMenuOpen ? "animate-slide-in" : "",
                    )}
                    style={{ animationDelay: `${(index + subIndex) * 0.1}s` }}
                  >
                    {subItem.label}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => navigateTo(item.href)}
                className={cn(
                  "text-xl font-medium transition-colors duration-300",
                  isActive(item.href) ? "text-primary" : "hover:text-primary",
                  isMobileMenuOpen ? "animate-slide-in" : "",
                )}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {item.label}
              </Link>
            )}
          </React.Fragment>
        ))}
      </div>
    </nav>
  );
};

export default Navbar;
